/**
 * Auth context: stores the bearer token + current user and exposes
 * `setSession` (called after a successful login/register mutation),
 * `logout`, and `currentUser`. The token is persisted in `localStorage`
 * (via the API client's token helpers) so a refresh keeps the user
 * signed in.
 *
 * On mount, if a token exists we call `GET /api/me` to verify it and
 * hydrate the user. A 401 here clears the token silently.
 *
 * Note: the login/register API calls live in the screen layer (as
 * `@tanstack/react-query` mutations). This context is responsible only
 * for the *state* — the screen calls `setSession(token, user)` on a
 * successful mutation.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiRequestError,
  api,
  getStoredToken,
  setStoredToken,
} from "../api/client";
import { setStudioTimezone } from "../lib/timezone";
import type { User } from "../lib/types";

export type AuthStatus = "loading" | "authed" | "anon";

interface AuthContextValue {
  /** The authenticated user, or `null` when signed out / loading. */
  currentUser: User | null;
  /** Alias kept for ergonomics / existing call sites. */
  user: User | null;
  token: string | null;
  status: AuthStatus;
  /**
   * Adopt a token + user pair (e.g. from a successful login/register
   * mutation). Persists the token to `localStorage` and flips the
   * status to `authed`.
   */
  setSession: (token: string, user: User) => void;
  /**
   * Replace the current user without touching the token — used after a
   * profile edit (`PATCH /api/me`, issue #224) that is orthogonal to the
   * session. The stored bearer token is left exactly as it was.
   */
  updateUser: (user: User) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() =>
    getStoredToken() ? "loading" : "anon",
  );

  // Hydrate the user on mount / token change.
  useEffect(() => {
    if (!token) {
      setUser(null);
      setStatus("anon");
      return;
    }

    let cancelled = false;
    setStatus("loading");

    api
      .get<User>("/api/me", { token })
      .then((u) => {
        if (cancelled) return;
        // The studio timezone drives every rendered lesson time (issue
        // #203). Missing on older responses: `setStudioTimezone` then
        // leaves the default in place.
        setStudioTimezone(u.studio_timezone);
        setUser(u);
        setStatus("authed");
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 = token is gone (rotated, deleted, garbage). Drop it.
        if (err instanceof ApiRequestError && err.status === 401) {
          setStoredToken(null);
          setToken(null);
        }
        setUser(null);
        setStatus("anon");
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const setSession = useCallback((nextToken: string, nextUser: User) => {
    setStoredToken(nextToken);
    setToken(nextToken);
    setUser(nextUser);
    setStatus("authed");
  }, []);

  const updateUser = useCallback((nextUser: User) => {
    setUser(nextUser);
    setStatus("authed");
  }, []);

  const logout = useCallback(async () => {
    if (token) {
      try {
        await api.delete<void>("/api/session", { token });
      } catch {
        /* even if server-side invalidation fails, drop the local copy. */
      }
    }
    setStoredToken(null);
    setToken(null);
    setUser(null);
    setStatus("anon");
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser: user,
      user,
      token,
      status,
      setSession,
      updateUser,
      logout,
    }),
    [user, token, status, setSession, updateUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
